import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { CrudTotalSchemaItem } from "@/shared/components/CRUD/CrudTotal";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import type { ContractPaymentsListRow } from "@/admin/entities/contractPayments";
import type { ContractPaymentsListQueryType } from "@/types/graphql/admin.generated";
import {
  buildActionsColumn,
  buildAmountColumn,
  buildExpiredAtColumn,
  buildInvoiceDateColumn,
  buildInvoiceNumberColumn,
  buildLinesColumn,
  buildStatusColumn,
} from "../domains/contractPaymentColumns";
import { formatMoney } from "../domains/billingFormat";
import { ContractPaymentStatusLink } from "../domains/ContractPaymentCardLinks";

export function getTableData(data: unknown): {
  nodes: ContractPaymentsListRow[];
  count: number;
} {
  const typed = data as ContractPaymentsListQueryType;
  const result = typed?.contractPayments;
  if (!result?.nodes) return { nodes: [], count: 0 };
  return { nodes: result.nodes, count: result.count };
}

export function mapColumns(payment: ContractPaymentsListRow) {
  return [
    payment.id,
    payment,
    payment,
    payment.invoiceDate,
    payment.expiredAt,
    payment,
    payment,
    payment,
    payment,
    payment,
  ];
}

export const getColumnsConfig =
  (): ColumnDefinition<ContractPaymentsListRow>[] => [
    { name: "id", label: "ID", options: { display: false, filter: false } },
    {
      name: "organization",
      label: "Организация",
      options: {
        sort: false,
        customBodyRender: (data: unknown) => {
          const payment = data as ContractPaymentsListRow;
          return (
            <CrudLink
              path={PagePaths.organizations}
              tab="contractPayments"
              id={payment.organizationId}
            >
              {payment.organization?.title ?? ""}
            </CrudLink>
          );
        },
      },
    },
    {
      name: "contract",
      label: "Контракт",
      options: {
        sort: false,
        customBodyRender: (data: unknown) => {
          const payment = data as ContractPaymentsListRow;
          return payment.contract?.title ?? "";
        },
      },
    },
    buildInvoiceDateColumn(),
    buildExpiredAtColumn(),
    buildAmountColumn(),
    buildStatusColumn(),
    buildLinesColumn(),
    buildInvoiceNumberColumn(),
    buildActionsColumn(),
  ];

/**
 * Итоги по текущей странице списка, а не по всему фильтру: сервер агрегатов
 * по платежам контрактов не отдаёт, а считать их на клиенте по всем
 * страницам нечем. Подписи это прямо говорят.
 */
export const getTotalSchema = (): CrudTotalSchemaItem[] => [
  {
    xs: 12,
    md: 4,
    label: "Платежей на странице",
    value: (data: unknown) => getTableData(data).nodes.length,
  },
  {
    xs: 12,
    md: 4,
    label: "Просроченных на странице",
    value: (data: unknown) =>
      getTableData(data).nodes.filter((payment) => payment.expired).length,
  },
  {
    xs: 12,
    md: 4,
    label: "Сумма на странице",
    value: (data: unknown) => {
      const nodes = getTableData(data).nodes;
      const byCurrency = new Map<string, number>();
      for (const payment of nodes) {
        const currency = payment.amount.currency;
        byCurrency.set(
          currency,
          (byCurrency.get(currency) ?? 0) + payment.amount.value
        );
      }
      const parts = [...byCurrency.entries()].map(([currency, value]) =>
        formatMoney({ value, currency })
      );
      return parts.join(", ");
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Платежи по контрактам",
  refetchList: "Обновить список",
  cancel: "Отмена",
  editItem: "Платёж",
  showTitle: "Детали",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<ContractPaymentsListRow> => ({
    main: {
      title: "Платёж",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography noWrap>{formatMoney(row.amount)}</Typography>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.organization?.title ?? ""}
          </Typography>
        </Stack>
      ),
    },
    status: {
      title: "Статус",
      renderCell: (row) => <ContractPaymentStatusLink payment={row} />,
    },
  });
